import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom/client";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import Papa from "papaparse";
import "../styles/ParsiMapPages.css";
import MapControlsDrawer from "./MapControlsDrawer";
import BuildingTooltipCard from "./BuildingTooltipCard";
import MapTypeToggle from "./MapTypeToggle";
import { featureToPolygonPaths } from "../utils/geoJsonPaths";
import { decisionFromServiceabilityFlag } from "../utils/serviceabilityDecision";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Tehran center coordinates
const TEHRAN_CENTER = { lat: 35.7, lng: 51.4 };

// Sidewalk offset in meters (cables run along building walls, not road center)
const SIDEWALK_OFFSET_METERS = 3.5;
const SERVICEABILITY_COLORS = {
  serviceable: '#10b981',
  not_serviceable: '#ef4444',
  unknown: '#f59e0b',
};

/**
 * Find intersection point of two lines (each defined by two points)
 * @returns {Object|null} {lat, lng} or null if parallel
 */
function lineIntersection(p1, p2, p3, p4) {
  const x1 = p1.lng, y1 = p1.lat;
  const x2 = p2.lng, y2 = p2.lat;
  const x3 = p3.lng, y3 = p3.lat;
  const x4 = p4.lng, y4 = p4.lat;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-12) return null; // Parallel lines

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

  return {
    lat: y1 + t * (y2 - y1),
    lng: x1 + t * (x2 - x1)
  };
}

/**
 * Clean up route path to remove unnecessary zigzag crossings
 *
 * When FAT and building are on the same side of a street, the offline router
 * may still generate a path that crosses to the other side and back.
 * This function simplifies such paths by keeping only the necessary segments.
 *
 * Algorithm:
 * 1. Calculate the "dominant side" - the side where both FAT and building are
 * 2. For each segment of the path, check if it goes to the "wrong" side
 * 3. If the path crosses to wrong side and back, remove those segments
 *
 * @param {Array} routePath - Array of {lat, lng} coordinates
 * @param {Object} fatPos - {lat, lng} of FAT
 * @param {Object} buildingPos - {lat, lng} of building
 * @returns {Array} Cleaned route path
 */
function cleanupRoutePath(routePath, fatPos, buildingPos) {
  if (!routePath || routePath.length < 3) {
    return routePath;
  }

  // Calculate the general direction from FAT to building
  const fatToBuildingVec = {
    lat: buildingPos.lat - fatPos.lat,
    lng: buildingPos.lng - fatPos.lng
  };

  // Calculate perpendicular vector (to determine "left" and "right" of the path)
  const perpVec = {
    lat: -fatToBuildingVec.lng,
    lng: fatToBuildingVec.lat
  };

  // Normalize perpendicular vector
  const perpLen = Math.sqrt(perpVec.lat * perpVec.lat + perpVec.lng * perpVec.lng);
  if (perpLen < 1e-10) {
    return routePath;
  }
  perpVec.lat /= perpLen;
  perpVec.lng /= perpLen;

  // Calculate which "side" each point is on (relative to the FAT-building line)
  // Positive = one side, Negative = other side
  const getSide = (point) => {
    const toPoint = {
      lat: point.lat - fatPos.lat,
      lng: point.lng - fatPos.lng
    };
    return toPoint.lat * perpVec.lat + toPoint.lng * perpVec.lng;
  };

  // Get the side values for all path points
  const sides = routePath.map(p => getSide(p));

  // Find segments where the path goes to the "wrong" side and comes back
  // A "wrong" crossing is when path goes positive->negative->positive or vice versa
  // and both FAT and building are on the same side

  const fatSide = getSide(fatPos);
  const buildingSide = getSide(buildingPos);

  // If FAT and building are on opposite sides, no cleanup needed
  // (the crossing is necessary)
  if (fatSide * buildingSide < 0) {
    console.log('🔀 FAT and building on opposite sides - no cleanup needed');
    return routePath;
  }

  // FAT and building are on the same side - look for unnecessary crossings
  const sameSideSign = fatSide >= 0 ? 1 : -1; // The "correct" side

  // Find segments that dip to the wrong side
  const cleanedPath = [routePath[0]]; // Always keep first point

  let i = 1;
  while (i < routePath.length - 1) {
    const currentSide = sides[i];
    const currentOnWrongSide = (currentSide * sameSideSign) < 0;

    if (currentOnWrongSide) {
      // This point is on the wrong side - skip it and following points until we're back
      console.log(`⏭️ Skipping point ${i} - on wrong side of path`);
      let j = i + 1;
      while (j < routePath.length - 1 && (sides[j] * sameSideSign) < 0) {
        console.log(`⏭️ Skipping point ${j} - still on wrong side`);
        j++;
      }
      i = j; // Skip to the point where we're back on correct side
    } else {
      cleanedPath.push(routePath[i]);
      i++;
    }
  }

  // Always keep last point (building)
  cleanedPath.push(routePath[routePath.length - 1]);

  console.log(`🧹 Cleaned path: ${routePath.length} -> ${cleanedPath.length} points`);

  return cleanedPath;
}

/**
 * Snap path to Manhattan grid (perpendicular + parallel only)
 * Converts diagonal segments to L-shaped paths
 *
 * Rule: Cable paths should either:
 * - Move ALONG streets (parallel)
 * - Cross streets at 90° (perpendicular)
 * - NO diagonal movement allowed
 *
 * @param {Array} routePath - Array of {lat, lng} coordinates
 * @returns {Array} Path with only perpendicular/parallel segments
 */
function snapToManhattan(routePath) {
  if (!routePath || routePath.length < 2) {
    return routePath;
  }

  // Threshold: segments shorter than ~2 meters don't need splitting
  const threshold = 0.00002; // ~2m at Tehran latitude

  const snappedPath = [routePath[0]];

  for (let i = 1; i < routePath.length; i++) {
    const prev = snappedPath[snappedPath.length - 1];
    const curr = routePath[i];

    const dx = curr.lng - prev.lng;
    const dy = curr.lat - prev.lat;

    // Check if segment is diagonal (both dx and dy significant)
    if (Math.abs(dx) > threshold && Math.abs(dy) > threshold) {
      // Add intermediate point to make L-shape
      // Choose direction based on which component is larger
      if (Math.abs(dx) > Math.abs(dy)) {
        // Go horizontal first, then vertical
        snappedPath.push({ lat: prev.lat, lng: curr.lng });
      } else {
        // Go vertical first, then horizontal
        snappedPath.push({ lat: curr.lat, lng: prev.lng });
      }
    }

    snappedPath.push(curr);
  }

  console.log(`📐 Manhattan snap: ${routePath.length} -> ${snappedPath.length} points`);
  return snappedPath;
}

/**
 * Offset a route path from road center to sidewalk (building side)
 *
 * IMPORTANT: This function preserves perpendicular/parallel geometry by:
 * 1. Offsetting entire LINE SEGMENTS (not individual points)
 * 2. Finding intersections of adjacent offset segments for corner points
 * 3. This ensures cables run on sidewalks without creating diagonal paths
 *
 * @param {Array} routePath - Array of {lat, lng} coordinates
 * @param {Object} buildingPosition - {lat, lng} of destination building
 * @param {number} offsetMeters - Distance to offset (default 3.5m)
 * @returns {Array} Offset route path with preserved geometry
 */
function offsetPathToSidewalk(routePath, buildingPosition, offsetMeters = SIDEWALK_OFFSET_METERS) {
  if (!routePath || routePath.length < 3 || !buildingPosition) {
    return routePath;
  }

  // Convert offset from meters to approximate degrees (at Tehran's latitude)
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLng = 111320 * Math.cos(35.7 * Math.PI / 180);
  const offsetLatDeg = offsetMeters / metersPerDegreeLat;
  const offsetLngDeg = offsetMeters / metersPerDegreeLng;

  // Step 1: Calculate perpendicular offset direction for each segment (toward building)
  const segmentOffsets = [];
  for (let i = 0; i < routePath.length - 1; i++) {
    const p1 = routePath[i];
    const p2 = routePath[i + 1];

    // Segment direction vector
    const dx = p2.lng - p1.lng;
    const dy = p2.lat - p1.lat;
    const len = Math.sqrt(dx * dx + dy * dy);

    if (len < 1e-10) {
      segmentOffsets.push({ lat: 0, lng: 0 });
      continue;
    }

    // Two perpendicular directions (normalized)
    const perp1 = { lat: dx / len, lng: -dy / len };
    const perp2 = { lat: -dx / len, lng: dy / len };

    // Segment midpoint
    const mid = { lat: (p1.lat + p2.lat) / 2, lng: (p1.lng + p2.lng) / 2 };

    // Test which perpendicular points toward building
    const test1 = { lat: mid.lat + perp1.lat * 0.0001, lng: mid.lng + perp1.lng * 0.0001 };
    const test2 = { lat: mid.lat + perp2.lat * 0.0001, lng: mid.lng + perp2.lng * 0.0001 };

    const dist1 = (test1.lat - buildingPosition.lat) ** 2 + (test1.lng - buildingPosition.lng) ** 2;
    const dist2 = (test2.lat - buildingPosition.lat) ** 2 + (test2.lng - buildingPosition.lng) ** 2;

    const perpToward = dist1 < dist2 ? perp1 : perp2;
    segmentOffsets.push({
      lat: perpToward.lat * offsetLatDeg,
      lng: perpToward.lng * offsetLngDeg
    });
  }

  // Step 2: Create offset line segments (parallel to originals, shifted toward building)
  const offsetSegments = [];
  for (let i = 0; i < routePath.length - 1; i++) {
    const p1 = routePath[i];
    const p2 = routePath[i + 1];
    const off = segmentOffsets[i];

    offsetSegments.push({
      p1: { lat: p1.lat + off.lat, lng: p1.lng + off.lng },
      p2: { lat: p2.lat + off.lat, lng: p2.lng + off.lng }
    });
  }

  // Step 3: Build the offset path maintaining perpendicular/parallel geometry
  const offsetPath = [];

  // First point: FAT position (unchanged - perpendicular crossing will connect to sidewalk)
  offsetPath.push({ ...routePath[0] });

  // Connect FAT to the offset road via perpendicular crossing
  // Use the start of the first offset segment
  if (offsetSegments.length > 0) {
    offsetPath.push({ ...offsetSegments[0].p1 });
  }

  // For each corner (middle points): Find intersection of adjacent offset segments
  // This preserves perpendicular/parallel geometry at corners
  for (let i = 0; i < offsetSegments.length - 1; i++) {
    const seg1 = offsetSegments[i];
    const seg2 = offsetSegments[i + 1];

    // Find intersection of the two offset lines (extended infinitely)
    const intersection = lineIntersection(seg1.p1, seg1.p2, seg2.p1, seg2.p2);

    if (intersection) {
      offsetPath.push(intersection);
    } else {
      // Lines are parallel - use endpoint of first segment
      offsetPath.push({ ...seg1.p2 });
    }
  }

  // Connect sidewalk to building via perpendicular crossing
  // Use the end of the last offset segment
  if (offsetSegments.length > 0) {
    offsetPath.push({ ...offsetSegments[offsetSegments.length - 1].p2 });
  }

  // Last point: Building position (unchanged)
  offsetPath.push({ ...routePath[routePath.length - 1] });

  return offsetPath;
}

/**
 * Main map content component (must be inside APIProvider)
 */
function MapContent({
  lat, setLat, lng, setLng,
  address, setAddress,
  reverseAddress, setReverseAddress,
  addressOptions, setAddressOptions,
  loadingAddress, setLoadingAddress,
  loadingReverse, setLoadingReverse,
  error, setError,
  selectedProvince, setSelectedProvince,
  selectedCity, setSelectedCity,
  coverageData, setCoverageData,
  isCalculating, setIsCalculating,
  fatCoverageError, setFatCoverageError,
  serviceProviderPolygons, setServiceProviderPolygons,
  cablingProgress, setCablingProgress,
  isCablingRulesRunning, setIsCablingRulesRunning,
  abortControllerRef,
  mapType, setMapType,
  mapSettings, onSettingsChange,
}) {
  const map = useMap();

  // Generate map styles for hiding labels
  const getMapStyles = useCallback(() => {
    if ((mapType === 'roadmap' || mapType === 'terrain') && mapSettings.hideMapLabels) {
      return [
        {
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ];
    }
    return [];
  }, [mapType, mapSettings.hideMapLabels]);

  // Refs for map objects
  const searchMarkerRef = useRef(null);
  const searchCircleRef = useRef(null);
  const fatMarkerRef = useRef(null);
  const coveragePolygonRef = useRef(null);
  const coveredBuildingsRef = useRef([]);
  const excludedBuildingsRef = useRef([]);
  const dataLayersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const layersLoadedRef = useRef(false);

  // Refs for Google Maps layers
  const trafficLayerRef = useRef(null);
  const transitLayerRef = useRef(null);

  // Refs for debug path visualization
  const debugPathRef = useRef(null);
  const debugDistanceLabelRef = useRef(null);
  const debugCrossingMarkersRef = useRef([]);
  const debugCrossingLabelsRef = useRef([]);

  // Ref to always access latest coverageData (for closures in event handlers)
  const coverageDataRef = useRef(null);

  // Ref for animation frame (declared early for clearDebugPath)
  const animationFrameRef = useRef(null);

  // Clear debug path visualization (defined early to be available in useEffect)
  const clearDebugPath = useCallback(() => {
    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (debugPathRef.current) {
      debugPathRef.current.setMap(null);
      debugPathRef.current = null;
    }
    if (debugDistanceLabelRef.current) {
      debugDistanceLabelRef.current.setMap(null);
      debugDistanceLabelRef.current = null;
    }
    debugCrossingMarkersRef.current.forEach(marker => {
      if (marker.setMap) marker.setMap(null);
    });
    debugCrossingMarkersRef.current = [];
    debugCrossingLabelsRef.current.forEach(label => {
      if (label.setMap) label.setMap(null);
      else if (label.close) label.close();
    });
    debugCrossingLabelsRef.current = [];
  }, []);

  // CSV to GeoJSON conversion using Papa Parse
  const csvToGeoJSON = (csvText) => {
    const parsed = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
    });

    const features = parsed.data.map((row) => {
      const lat = parseFloat(row.lat || row.latitude);
      const lng = parseFloat(row.lng || row.longitude);

      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [lng, lat] },
        properties: row
      };
    });

    return { type: "FeatureCollection", features };
  };

  // Load GeoJSON and CSV data layers
  const loadGeoData = useCallback(async () => {
    if (!map || layersLoadedRef.current) return;
    layersLoadedRef.current = true;

    try {
      const filesRes = await fetch("/data/files.json");
      if (!filesRes.ok) {
        console.warn("files.json not found - additional layers will not be loaded");
        return;
      }

      const files = await filesRes.json();

      // Load GeoJSON files
      if (files.geojson && Array.isArray(files.geojson)) {
        for (const filename of files.geojson) {
          try {
            const res = await fetch(`/data/${filename}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            // Store mobintest.geojson data for service coverage checking
            if (filename === 'mobintest.geojson') {
              setServiceProviderPolygons(data);
              console.log('Loaded MobinNet coverage polygons');
            }

            // Add GeoJSON data as polygons.
            // featureToPolygonPaths validates coordinates and skips degenerate
            // rings, so malformed source features can't crash layer loading.
            if (data.features) {
              data.features.forEach((feature) => {
                featureToPolygonPaths(feature).forEach(path => {
                  const polygon = new window.google.maps.Polygon({
                    paths: path,
                    fillColor: "#088",
                    fillOpacity: 0.4,
                    strokeWeight: 1,
                    strokeColor: "#088",
                    map: map,
                  });
                  dataLayersRef.current.push(polygon);
                });
              });
            }
          } catch (err) {
            console.error(`Error loading GeoJSON file ${filename}:`, err.message);
          }
        }
      }

      // Load CSV files
      if (files.csv && Array.isArray(files.csv)) {
        for (const filename of files.csv) {
          try {
            const res = await fetch(`/data/${filename}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const csvText = await res.text();
            const geojson = csvToGeoJSON(csvText);

            // Add CSV points as markers
            geojson.features.forEach((feature) => {
              const [lng, lat] = feature.geometry.coordinates;
              if (!isNaN(lat) && !isNaN(lng)) {
                const marker = new window.google.maps.Marker({
                  position: { lat, lng },
                  map: map,
                  icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: "#e63946",
                    fillOpacity: 1,
                    strokeColor: "#fff",
                    strokeWeight: 1,
                  },
                });
                dataLayersRef.current.push(marker);
              }
            });
          } catch (err) {
            console.error(`Error loading CSV file ${filename}:`, err.message);
          }
        }
      }
    } catch (err) {
      console.error("Error loading layer files:", err.message);
    }
  }, [map, setServiceProviderPolygons]);

  // Initialize map and load data
  useEffect(() => {
    if (!map) return;

    // Load data layers
    loadGeoData();

    // Create info window for building tooltips (disableAutoPan prevents map from scrolling when tooltip is near screen edge)
    infoWindowRef.current = new window.google.maps.InfoWindow({
      disableAutoPan: true
    });

    // Click handler
    const clickListener = map.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setLat(lat.toFixed(6));
      setLng(lng.toFixed(6));
      showPoint(lat, lng, "نقطه انتخاب‌شده");
      fetchReverseAddress(lat, lng);

      // Clear debug path and close info window when clicking on map (not on buildings)
      clearDebugPath();
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    });

    return () => {
      window.google.maps.event.removeListener(clickListener);
      // Clean up data layers
      dataLayersRef.current.forEach(layer => {
        if (layer.setMap) layer.setMap(null);
      });
      dataLayersRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loadGeoData, clearDebugPath]);

  // Keep coverageDataRef synced with latest coverageData (for closures)
  useEffect(() => {
    coverageDataRef.current = coverageData;
  }, [coverageData]);

  // Apply map settings (traffic, transit layers, tilt, labels, etc.)
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    // Handle Traffic Layer
    if (mapSettings.showTraffic) {
      if (!trafficLayerRef.current) {
        trafficLayerRef.current = new window.google.maps.TrafficLayer();
      }
      trafficLayerRef.current.setMap(map);
    } else if (trafficLayerRef.current) {
      trafficLayerRef.current.setMap(null);
    }

    // Handle Transit Layer
    if (mapSettings.showTransit) {
      if (!transitLayerRef.current) {
        transitLayerRef.current = new window.google.maps.TransitLayer();
      }
      transitLayerRef.current.setMap(map);
    } else if (transitLayerRef.current) {
      transitLayerRef.current.setMap(null);
    }

    // Handle Terrain Mode (switch map type)
    if (mapSettings.showTerrain && (mapType === 'roadmap' || mapType === 'terrain')) {
      if (mapType !== 'terrain') {
        setMapType('terrain');
      }
    } else if (!mapSettings.showTerrain && mapType === 'terrain') {
      setMapType('roadmap');
    }

    // Note: hideMapLabels is now handled via styles prop on Map component
    // This avoids Cloud-based styling conflicts

    // Handle Satellite mode settings
    if (mapType === 'satellite' || mapType === 'hybrid') {
      // Labels and Roads: switch between satellite (no labels) and hybrid (with labels/roads)
      const shouldBeHybrid = mapSettings.showLabels || mapSettings.showRoads;
      if (shouldBeHybrid && mapType === 'satellite') {
        setMapType('hybrid');
      } else if (!shouldBeHybrid && mapType === 'hybrid') {
        setMapType('satellite');
      }

      // Handle 45° tilt view
      if (mapSettings.tilt45) {
        map.setTilt(45);
      } else {
        map.setTilt(0);
      }
    }

    // Cleanup on unmount
    return () => {
      if (trafficLayerRef.current) {
        trafficLayerRef.current.setMap(null);
      }
      if (transitLayerRef.current) {
        transitLayerRef.current.setMap(null);
      }
    };
  }, [map, mapSettings, mapType, setMapType]);

  // Show a point on the map
  const showPoint = useCallback((latNum, lngNum, label = "نقطه انتخابی") => {
    if (!map) return;

    const position = { lat: parseFloat(latNum), lng: parseFloat(lngNum) };

    // Remove existing search marker
    if (searchMarkerRef.current) {
      searchMarkerRef.current.setMap(null);
    }
    if (searchCircleRef.current) {
      searchCircleRef.current.setMap(null);
    }

    // Add new marker
    searchMarkerRef.current = new window.google.maps.Marker({
      position,
      map,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: "#ff0000",
        fillOpacity: 0.9,
        strokeWeight: 0,
      },
      title: label,
    });

    // Add buffer circle
    searchCircleRef.current = new window.google.maps.Circle({
      center: position,
      radius: 50,
      fillColor: "#ff0000",
      fillOpacity: 0.2,
      strokeWeight: 0,
      map,
    });

    // Pan to position
    map.panTo(position);
    map.setZoom(16);
  }, [map]);

  // Fetch reverse address
  const fetchReverseAddress = async (lat, lng) => {
    setLoadingReverse(true);
    setError(null);
    try {
      const response = await fetch(`/api/reverse?lat=${lat}&lng=${lng}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setReverseAddress(data.address || "آدرس پیدا نشد");
    } catch (err) {
      console.error("Error fetching reverse address:", err);
      const errorMsg = "خطا در دریافت آدرس";
      setError(errorMsg);
      setReverseAddress(errorMsg);
    } finally {
      setLoadingReverse(false);
    }
  };

  // Handle lat/lng search
  const handleLatLngSearch = () => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (!isNaN(latNum) && !isNaN(lngNum)) {
      showPoint(latNum, lngNum);
      fetchReverseAddress(latNum, lngNum);
    }
  };

  // Handle address search
  const handleAddressSearch = async () => {
    if (!address.trim()) return;

    setLoadingAddress(true);
    setError(null);
    try {
      const params = new URLSearchParams({ search_text: address });
      if (selectedCity) params.set("city", selectedCity);
      const response = await fetch(`/api/address?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data.results) && data.results.length > 0) {
        setAddressOptions(data.results);
      } else if (data.location?.lat && data.location?.lng) {
        showPoint(data.location.lat, data.location.lng, address);
        setLat(data.location.lat.toFixed(6));
        setLng(data.location.lng.toFixed(6));
        setReverseAddress(data.address || "آدرس پیدا شد");
      } else if (data.error) {
        console.error("Server error:", data);
        setError(data.error || "خطا در جستجوی آدرس");
      } else {
        setError("نتیجه‌ای یافت نشد.");
      }
    } catch (err) {
      console.error("Error searching address:", err);
      setError("خطا در جستجوی آدرس");
    } finally {
      setLoadingAddress(false);
    }
  };

  // Handle address selection from options
  const handleAddressSelection = (opt) => {
    showPoint(opt.lat, opt.lng, opt.address);
    setLat(opt.lat.toFixed(6));
    setLng(opt.lng.toFixed(6));
    setReverseAddress(opt.address);
    setAddressOptions([]);
  };

  // Clear coverage visualization
  const clearCoverageVisualization = useCallback(() => {
    if (fatMarkerRef.current) {
      fatMarkerRef.current.setMap(null);
      fatMarkerRef.current = null;
    }
    if (coveragePolygonRef.current) {
      coveragePolygonRef.current.setMap(null);
      coveragePolygonRef.current = null;
    }
    coveredBuildingsRef.current.forEach(p => p.setMap(null));
    coveredBuildingsRef.current = [];
    excludedBuildingsRef.current.forEach(p => p.setMap(null));
    excludedBuildingsRef.current = [];

    // Also clear debug path
    if (debugPathRef.current) {
      debugPathRef.current.setMap(null);
      debugPathRef.current = null;
    }
    if (debugDistanceLabelRef.current) {
      debugDistanceLabelRef.current.setMap(null);
      debugDistanceLabelRef.current = null;
    }
    debugCrossingMarkersRef.current.forEach(marker => marker.setMap(null));
    debugCrossingMarkersRef.current = [];
    debugCrossingLabelsRef.current.forEach(label => label.setMap(null));
    debugCrossingLabelsRef.current = [];
  }, []);

  // Calculate building centroid from polygon coordinates
  const getBuildingCentroid = useCallback((coords) => {
    if (!coords || coords.length === 0) return null;

    let sumLat = 0, sumLng = 0;
    coords.forEach(coord => {
      sumLat += coord.lat;
      sumLng += coord.lng;
    });

    return {
      lat: sumLat / coords.length,
      lng: sumLng / coords.length
    };
  }, []);

  // Draw animated debug path from FAT to building following road network
  const drawDebugPath = useCallback((buildingCoords, validationData, serviceabilityDecision) => {
    // Use ref to access latest coverageData (avoids stale closure in click handlers)
    const latestCoverageData = coverageDataRef.current;
    if (!map || !latestCoverageData?.fatCoordinates) {
      console.warn('⚠️ drawDebugPath: Cannot draw path - missing map or fatCoordinates');
      return;
    }

    // Clear existing debug visualization
    clearDebugPath();
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const fatPos = latestCoverageData.fatCoordinates;
    const buildingCentroid = getBuildingCentroid(buildingCoords);
    if (!buildingCentroid) return;

    // Get rules for crossings info
    const rules = validationData?.rules;

    // Get route path from backend (follows roads)
    let routePath = rules?.rule2?.routePath;

    // Fallback to straight line if no route path available
    if (!routePath || routePath.length < 2) {
      console.warn('⚠️ Using fallback straight line - routePath not available!');
      routePath = [
        { lat: fatPos.lat, lng: fatPos.lng },
        { lat: buildingCentroid.lat, lng: buildingCentroid.lng }
      ];
    } else {
      console.log('🛤️ Got routePath from backend, points:', routePath.length);

      // Step 1: Clean up unnecessary crossings - if path zigzags when FAT and building are on same side
      // This ensures the visual path matches the crossing detection logic
      routePath = cleanupRoutePath(routePath, fatPos, buildingCentroid);

      // Step 2: Offset the path from road center to sidewalk (building side)
      // Cables run along building walls, not through the middle of streets
      // NOTE: This may create diagonal segments (e.g., from sidewalk to building centroid)
      routePath = offsetPathToSidewalk(routePath, buildingCentroid);
      console.log('🛤️ Path offset to sidewalk, points:', routePath.length);

      // Step 3: Snap to Manhattan grid LAST - convert ALL diagonal segments to L-shaped
      // This fixes any diagonals created by offsetPathToSidewalk (e.g., connection to building)
      routePath = snapToManhattan(routePath);
      console.log('📐 Path snapped to Manhattan grid, points:', routePath.length);
    }

    // Calculate total path distance for display
    let totalDistance = 0;
    for (let i = 0; i < routePath.length - 1; i++) {
      const R = 6371000;
      const dLat = (routePath[i+1].lat - routePath[i].lat) * Math.PI / 180;
      const dLng = (routePath[i+1].lng - routePath[i].lng) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(routePath[i].lat * Math.PI / 180) * Math.cos(routePath[i+1].lat * Math.PI / 180) *
                Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      totalDistance += R * c;
    }

    // Find violation point index (first crossing that violates rules)
    let violationIndex = -1;
    const crossings = rules?.rule4?.crossings || [];
    const maxStreetWidth = rules?.rule4?.maxStreetWidthThreshold || 6;

    // Find first violation crossing and its position on the path
    for (const crossing of crossings) {
      if (!crossing.crossingPoint || (crossing.width || 0) <= maxStreetWidth) continue;

      // Find nearest point on route path to this crossing
      const crossingLat = crossing.crossingPoint[1];
      const crossingLng = crossing.crossingPoint[0];

      let minDist = Infinity;
      let nearestIdx = -1;

      for (let i = 0; i < routePath.length; i++) {
        const dx = routePath[i].lng - crossingLng;
        const dy = routePath[i].lat - crossingLat;
        const dist = Math.sqrt(dx*dx + dy*dy);
        if (dist < minDist) {
          minDist = dist;
          nearestIdx = i;
        }
      }

      if (nearestIdx !== -1 && (violationIndex === -1 || nearestIdx < violationIndex)) {
        violationIndex = nearestIdx;
      }
    }

    // If no violations, the entire path is green
    const hasViolation = violationIndex > 0;

    // Animation settings
    const ANIMATION_DURATION = 2000; // 2 seconds
    const startTime = performance.now();

    // Create polylines for animation (green and red segments)
    const greenPath = new window.google.maps.Polyline({
      path: [],
      geodesic: true,
      strokeColor: '#10b981',
      strokeOpacity: 0,
      strokeWeight: 5,
      icons: [{
        icon: {
          path: 'M 0,-1 0,1',
          strokeOpacity: 1,
          strokeWeight: 5,
          strokeColor: '#10b981',
          scale: 3,
        },
        offset: '0',
        repeat: '12px'
      }],
      map: map,
      zIndex: 100
    });

    const redPath = new window.google.maps.Polyline({
      path: [],
      geodesic: true,
      strokeColor: '#ef4444',
      strokeOpacity: 0,
      strokeWeight: 5,
      icons: [{
        icon: {
          path: 'M 0,-1 0,1',
          strokeOpacity: 1,
          strokeWeight: 5,
          strokeColor: '#ef4444',
          scale: 3,
        },
        offset: '0',
        repeat: '12px'
      }],
      map: map,
      zIndex: 101
    });

    // Store refs for cleanup
    debugPathRef.current = greenPath;
    debugCrossingMarkersRef.current.push(redPath);

    // Animate the path drawing
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);

      // Calculate how many points to show based on progress
      const totalPoints = routePath.length;
      const pointsToShow = Math.floor(progress * totalPoints);

      // Determine which segment we're in
      const effectiveViolationIndex = hasViolation ? violationIndex : totalPoints;

      // Build green segment (up to violation or current progress)
      const greenPoints = [];
      const redPoints = [];

      for (let i = 0; i <= pointsToShow && i < totalPoints; i++) {
        if (i < effectiveViolationIndex) {
          greenPoints.push(routePath[i]);
        } else {
          // Start red segment with last green point for continuity
          if (redPoints.length === 0 && greenPoints.length > 0) {
            redPoints.push(greenPoints[greenPoints.length - 1]);
          }
          redPoints.push(routePath[i]);
        }
      }

      // Update polylines
      greenPath.setPath(greenPoints);
      redPath.setPath(redPoints);

      // Continue animation if not complete
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - add end marker
        const endMarker = new window.google.maps.Marker({
          position: routePath[routePath.length - 1],
          map: map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: SERVICEABILITY_COLORS[serviceabilityDecision],
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 8,
          },
          zIndex: 200
        });
        debugCrossingMarkersRef.current.push(endMarker);
        // Distance info is shown in building popup, no need for separate label
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Draw crossing point markers
    if (crossings.length > 0) {
      crossings.forEach((crossing, index) => {
        if (!crossing.crossingPoint) return;

        const crossingPos = {
          lat: crossing.crossingPoint[1],
          lng: crossing.crossingPoint[0]
        };

        const crossingWidth = crossing.width || 0;
        const isWidthOk = crossingWidth <= maxStreetWidth;

        // Create crossing marker
        const crossingMarker = new window.google.maps.Marker({
          position: crossingPos,
          map: map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: isWidthOk ? '#f59e0b' : '#dc2626',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
            scale: 10,
          },
          title: crossing.roadName || `عبور ${index + 1}`,
          zIndex: 1000 + index,
        });
        debugCrossingMarkersRef.current.push(crossingMarker);

        // Create crossing label
        const roadName = crossing.roadName || 'معبر ناشناخته';
        const widthText = crossingWidth > 0 ? `${crossingWidth.toFixed(1)}m` : 'نامشخص';

        const crossingLabel = new window.google.maps.InfoWindow({
          content: `
            <div style="
              background: linear-gradient(135deg, #1e1e1e, #2d2d2d);
              color: white;
              padding: 10px 14px;
              border-radius: 12px;
              font-family: 'Vazir', Tahoma, sans-serif;
              font-size: 12px;
              direction: rtl;
              text-align: right;
              box-shadow: 0 4px 20px rgba(0,0,0,0.4);
              border: 2px solid ${isWidthOk ? '#f59e0b' : '#dc2626'};
              min-width: 140px;
            ">
              <div style="
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 6px;
                padding-bottom: 6px;
                border-bottom: 1px solid rgba(255,255,255,0.2);
              ">
                <span style="font-size: 16px;">🛣️</span>
                <span style="font-weight: 600; color: #fbbf24;">${roadName}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="opacity: 0.8;">عرض معبر:</span>
                <span style="
                  background: ${isWidthOk ? '#065f46' : '#991b1b'};
                  padding: 2px 10px;
                  border-radius: 10px;
                  font-weight: 600;
                  font-size: 13px;
                ">${widthText}</span>
              </div>
              ${!isWidthOk ? `
                <div style="
                  margin-top: 8px;
                  padding-top: 6px;
                  border-top: 1px solid rgba(255,255,255,0.2);
                  color: #fca5a5;
                  font-size: 11px;
                  text-align: center;
                ">
                  ⚠️ عرض بیش از حد مجاز (${maxStreetWidth}m)
                </div>
              ` : ''}
            </div>
          `,
          position: crossingPos,
          disableAutoPan: true,
          pixelOffset: new window.google.maps.Size(0, -20),
        });
        crossingLabel.open(map);
        debugCrossingLabelsRef.current.push(crossingLabel);
      });
    }

    console.log(`Animated path: FAT → Building (${Math.round(totalDistance)}m), ${routePath.length} points, decision: ${serviceabilityDecision}`);
  }, [map, clearDebugPath, getBuildingCentroid]); // coverageData accessed via ref to avoid stale closures

  // Add building click listener (shows info popup and draws cable path)
  const addBuildingClickListener = useCallback((polygon, validationData, buildingCoords) => {
    if (!infoWindowRef.current) return;

    // Click handler: show building info and draw cable path
    polygon.addListener("click", (e) => {
      if (!validationData) return;

      // Close any existing info window first
      infoWindowRef.current.close();

      // Create tooltip container with building info
      const tooltipContainer = document.createElement('div');
      const root = ReactDOM.createRoot(tooltipContainer);

      // Handler to close the info window when close button is clicked
      const handleCloseTooltip = () => {
        infoWindowRef.current.close();
      };

      root.render(
        <BuildingTooltipCard
          validationData={validationData}
          onClose={handleCloseTooltip}
        />
      );

      infoWindowRef.current.setContent(tooltipContainer);
      infoWindowRef.current.setPosition(e.latLng);
      infoWindowRef.current.open(map);

      // Draw the cable path visualization
      if (buildingCoords) {
        const decision = decisionFromServiceabilityFlag(validationData.serviceable);
        drawDebugPath(buildingCoords, validationData, decision);
      }
    });
  }, [map, drawDebugPath]);

  // Apply cabling rules to buildings
  const handleApplyCablingRules = async (maxDropLength = 300, maxStreetWidth = 6) => {
    if (!coverageData || !coverageData.buildings) {
      alert('لطفاً ابتدا منطقه تحت پوشش را محاسبه کنید');
      return;
    }

    if (!coverageData.fatCoordinates) {
      alert('خطا: مختصات FAT در داده‌های پوشش یافت نشد. لطفاً دوباره پوشش را محاسبه کنید.');
      return;
    }

    const fatLat = coverageData.fatCoordinates.lat;
    const fatLng = coverageData.fatCoordinates.lng;

    if (isNaN(fatLat) || isNaN(fatLng)) {
      alert('مختصات FAT نامعتبر است');
      return;
    }

    console.log(`Using FAT coordinates from coverage calculation: (${fatLat}, ${fatLng})`);

    abortControllerRef.current = new AbortController();

    setIsCalculating(true);
    setIsCablingRulesRunning(true);
    setCablingProgress(0);
    setFatCoverageError(null);

    try {
      const allBuildings = [
        ...(coverageData.buildings.covered || []),
        ...(coverageData.buildings.excluded || [])
      ];

      console.log(`Applying cabling rules to ${allBuildings.length} buildings...`);

      const response = await fetch("/api/apply-cabling-rules", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fat: { lat: fatLat, lng: fatLng },
          range: coverageData.range || 300,
          buildings: allBuildings,
          hasDuct: false,
          maxDropLength: maxDropLength,
          maxStreetWidth: maxStreetWidth,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop();

        for (const message of messages) {
          if (message.startsWith('data: ')) {
            try {
              const event = JSON.parse(message.substring(6));

              if (event.type === 'progress') {
                setCablingProgress(event.percentage);
              } else if (event.type === 'complete') {
                setCablingProgress(100);

                // Clear and redraw buildings
                clearCoverageVisualization();

                // Add FAT marker back
                if (map) {
                  fatMarkerRef.current = new window.google.maps.Marker({
                    position: { lat: fatLat, lng: fatLng },
                    map,
                    icon: {
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 10,
                      fillColor: "#0066ff",
                      fillOpacity: 1,
                      strokeColor: "#ffffff",
                      strokeWeight: 2,
                    },
                    title: "FAT",
                  });

                  // Add serviceable buildings (green)
                  if (event.serviceableBuildings) {
                    event.serviceableBuildings.forEach((feature) => {
                      if (!feature?.geometry?.coordinates) return;

                      // Handle both Polygon and MultiPolygon geometries
                      let rings = [];
                      if (feature.geometry.type === 'Polygon') {
                        rings = [feature.geometry.coordinates[0]];
                      } else if (feature.geometry.type === 'MultiPolygon') {
                        rings = feature.geometry.coordinates.map(poly => poly[0]);
                      } else {
                        return;
                      }

                      rings.forEach(ring => {
                        if (!ring || ring.length < 3) return;
                        const coords = ring.map(c => ({ lat: c[1], lng: c[0] }));
                        const polygon = new window.google.maps.Polygon({
                          paths: coords,
                          fillColor: "#00cc00",
                          fillOpacity: 0.7,
                          strokeWeight: 1,
                          strokeColor: "#009900",
                          strokeOpacity: 1,
                          map,
                          clickable: true,
                          zIndex: 10,
                        });

                        const validation = feature.properties?.validation;
                        if (validation) {
                          const validationData = typeof validation === 'string' ? JSON.parse(validation) : validation;
                          addBuildingClickListener(polygon, validationData, coords);
                        }

                        coveredBuildingsRef.current.push(polygon);
                      });
                    });
                  }

                  // Add non-serviceable buildings (red)
                  if (event.nonServiceableBuildings) {
                    event.nonServiceableBuildings.forEach((feature) => {
                      if (!feature?.geometry?.coordinates) return;

                      // Handle both Polygon and MultiPolygon geometries
                      let rings = [];
                      if (feature.geometry.type === 'Polygon') {
                        rings = [feature.geometry.coordinates[0]];
                      } else if (feature.geometry.type === 'MultiPolygon') {
                        rings = feature.geometry.coordinates.map(poly => poly[0]);
                      } else {
                        return;
                      }

                      rings.forEach(ring => {
                        if (!ring || ring.length < 3) return;
                        const coords = ring.map(c => ({ lat: c[1], lng: c[0] }));
                        const polygon = new window.google.maps.Polygon({
                          paths: coords,
                          fillColor: "#ff0000",
                          fillOpacity: 0.7,
                          strokeWeight: 1,
                          strokeColor: "#cc0000",
                          strokeOpacity: 1,
                          map,
                          clickable: true,
                          zIndex: 10,
                        });

                        const validation = feature.properties?.validation;
                        if (validation) {
                          const validationData = typeof validation === 'string' ? JSON.parse(validation) : validation;
                          addBuildingClickListener(polygon, validationData, coords);
                        }

                        excludedBuildingsRef.current.push(polygon);
                      });
                    });
                  }
                }

                setCoverageData({
                  ...coverageData,
                  rulesApplied: true,
                  rulesResults: event.results,
                  stats: {
                    ...coverageData.stats,
                    totalBuildings: event.stats.total,
                    serviceableBuildings: event.stats.serviceable,
                    nonServiceableBuildings: event.stats.nonServiceable,
                    serviceablePercentage: event.stats.serviceablePercentage,
                  },
                });

                alert(`قوانین اعمال شد:\n✅ قابل سرویس: ${event.stats.serviceable}\n❌ غیرقابل سرویس: ${event.stats.nonServiceable}`);
              } else if (event.type === 'cancelled') {
                setFatCoverageError(event.message || "عملیات توسط کاربر لغو شد");
                alert("⚠️ عملیات متوقف شد");
                break;
              } else if (event.type === 'error') {
                setFatCoverageError(event.error || "خطا در اعمال قوانین کابل‌کشی");
                alert(`❌ خطا: ${event.details || 'عملیات متوقف شد'}`);
                break;
              }
            } catch (parseError) {
              console.error('Error parsing SSE message:', parseError);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error applying cabling rules:", err);
      if (err.name === 'AbortError') {
        setFatCoverageError("عملیات توسط کاربر لغو شد");
        alert("⚠️ عملیات متوقف شد");
      } else {
        setFatCoverageError("خطا در اعمال قوانین کابل‌کشی");
        alert("❌ خطا: عملیات متوقف شد. لطفاً دوباره تلاش کنید.");
      }
    } finally {
      setIsCalculating(false);
      setIsCablingRulesRunning(false);
      abortControllerRef.current = null;
    }
  };

  // Cancel cabling rules
  const handleCancelCablingRules = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('Cabling rules operation cancelled by user');
    }
  };

  // FAT Coverage calculation
  const handleFATCoverage = async (fatLat, fatLng, range, detectionMode, visionModel) => {
    setIsCalculating(true);
    setFatCoverageError(null);
    setCoverageData(null);

    try {
      const response = await fetch("/api/fat-coverage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat: fatLat, lng: fatLng, range, detectionMode, visionModel }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCoverageData(data);

      // Visualize FAT coverage on map
      if (map && data.coverage) {
        // Clear existing visualization
        clearCoverageVisualization();

        // Add FAT marker
        fatMarkerRef.current = new window.google.maps.Marker({
          position: { lat: fatLat, lng: fatLng },
          map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#0066ff",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
          title: "FAT",
        });

        // Add coverage polygon
        const coverageCoords = data.coverage.geometry.coordinates[0].map(c => ({
          lat: c[1],
          lng: c[0]
        }));

        coveragePolygonRef.current = new window.google.maps.Polygon({
          paths: coverageCoords,
          fillColor: "#0066ff",
          fillOpacity: 0,  // No fill to avoid color mixing with buildings
          strokeColor: "#0066ff",
          strokeWeight: 3,
          strokeOpacity: 1,
          map,
          zIndex: 1,  // Below buildings
        });

        // Log building counts
        console.log(`FAT Coverage: ${data.buildings?.covered?.length || 0} covered, ${data.buildings?.excluded?.length || 0} excluded buildings`);

        // Add covered buildings
        if (data.buildings?.covered && data.buildings.covered.length > 0) {
          console.log(`Rendering ${data.buildings.covered.length} covered buildings (GREEN)`);
          data.buildings.covered.forEach((feature, index) => {
            try {
              if (!feature?.geometry?.coordinates) {
                console.warn(`Covered building ${index}: missing geometry`);
                return;
              }

              // Handle both Polygon and MultiPolygon geometries
              let rings = [];
              if (feature.geometry.type === 'Polygon') {
                rings = [feature.geometry.coordinates[0]];
              } else if (feature.geometry.type === 'MultiPolygon') {
                rings = feature.geometry.coordinates.map(poly => poly[0]);
              } else {
                console.warn(`Covered building ${index}: unsupported geometry type`, feature.geometry.type);
                return;
              }

              rings.forEach(ring => {
                if (!ring || ring.length < 3) {
                  console.warn(`Covered building ${index}: invalid ring with ${ring?.length || 0} points`);
                  return;
                }
                const coords = ring.map(c => ({ lat: c[1], lng: c[0] }));
                const polygon = new window.google.maps.Polygon({
                  paths: coords,
                  fillColor: "#00cc00",
                  fillOpacity: 0.7,
                  strokeWeight: 1,
                  strokeColor: "#009900",
                  strokeOpacity: 1,
                  map,
                  zIndex: 10,  // Above coverage circle
                });
                coveredBuildingsRef.current.push(polygon);
              });
            } catch (err) {
              console.error(`Error rendering covered building ${index}:`, err, feature);
            }
          });
          console.log(`Successfully created ${coveredBuildingsRef.current.length} covered building polygons`);
        }

        // Add excluded buildings (RED)
        if (data.buildings?.excluded && data.buildings.excluded.length > 0) {
          console.log(`Rendering ${data.buildings.excluded.length} excluded buildings (RED)`);
          data.buildings.excluded.forEach((feature, index) => {
            try {
              if (!feature?.geometry?.coordinates) {
                return;
              }

              // Handle both Polygon and MultiPolygon geometries
              let rings = [];
              if (feature.geometry.type === 'Polygon') {
                rings = [feature.geometry.coordinates[0]];
              } else if (feature.geometry.type === 'MultiPolygon') {
                rings = feature.geometry.coordinates.map(poly => poly[0]);
              } else {
                return;
              }

              rings.forEach((ring) => {
                if (!ring || ring.length < 3) {
                  return;
                }
                const coords = ring.map(c => ({ lat: c[1], lng: c[0] }));
                const polygon = new window.google.maps.Polygon({
                  paths: coords,
                  fillColor: "#ff0000",
                  fillOpacity: 0.7,
                  strokeWeight: 1,
                  strokeColor: "#cc0000",
                  strokeOpacity: 1,
                  map,
                  zIndex: 10,  // Same level as covered buildings
                });
                excludedBuildingsRef.current.push(polygon);
              });
            } catch (err) {
              console.error(`Error rendering excluded building ${index}:`, err);
            }
          });
          console.log(`Created ${excludedBuildingsRef.current.length} excluded building polygons`);
        }

        // Fit map to coverage bounds (include buildings if any)
        const bounds = new window.google.maps.LatLngBounds();
        coverageCoords.forEach(coord => bounds.extend(coord));

        // Also include building bounds so they're visible
        excludedBuildingsRef.current.forEach(item => {
          // Only process Polygons (skip Markers)
          if (item.getPath) {
            const path = item.getPath();
            if (path) {
              path.forEach(latLng => bounds.extend(latLng));
            }
          } else if (item.getPosition) {
            // It's a Marker
            bounds.extend(item.getPosition());
          }
        });
        coveredBuildingsRef.current.forEach(item => {
          if (item.getPath) {
            const path = item.getPath();
            if (path) {
              path.forEach(latLng => bounds.extend(latLng));
            }
          } else if (item.getPosition) {
            bounds.extend(item.getPosition());
          }
        });

        console.log('Fitting map to bounds:', bounds.toString());
        map.fitBounds(bounds, { padding: 50 });
      }
    } catch (err) {
      console.error("Error calculating FAT coverage:", err);
      setFatCoverageError("خطا در محاسبه پوشش FAT");
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <>
      <Map
        defaultCenter={TEHRAN_CENTER}
        defaultZoom={11}
        mapTypeId={mapType}
        gestureHandling="greedy"
        disableDefaultUI={false}
        mapTypeControl={false}
        clickableIcons={false}
        styles={getMapStyles()}
        style={{ width: "100%", height: "100%" }}
      />

      <MapTypeToggle
        mapType={mapType}
        onMapTypeChange={setMapType}
        mapSettings={mapSettings}
        onSettingsChange={onSettingsChange}
      />

      <MapControlsDrawer
        lat={lat}
        lng={lng}
        address={address}
        reverseAddress={reverseAddress}
        addressOptions={addressOptions}
        loadingAddress={loadingAddress}
        loadingReverse={loadingReverse}
        error={error}
        onLatChange={setLat}
        onLngChange={setLng}
        onAddressChange={setAddress}
        onAddressSearch={handleAddressSearch}
        onLatLngSearch={handleLatLngSearch}
        onAddressSelection={handleAddressSelection}
        selectedProvince={selectedProvince}
        selectedCity={selectedCity}
        onProvinceSelect={setSelectedProvince}
        onCitySelect={setSelectedCity}
        coverageData={coverageData}
        isCalculating={isCalculating}
        fatCoverageError={fatCoverageError}
        onFATCalculate={handleFATCoverage}
        serviceProviderPolygons={serviceProviderPolygons}
        onApplyCablingRules={handleApplyCablingRules}
        onCancelCablingRules={handleCancelCablingRules}
        isCablingRulesRunning={isCablingRulesRunning}
        cablingProgress={cablingProgress}
      />
    </>
  );
}

/**
 * Main ParsiMapPage component with Google Maps
 */
export default function ParsiMapPage() {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [address, setAddress] = useState("");
  const [reverseAddress, setReverseAddress] = useState("");
  const [addressOptions, setAddressOptions] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [loadingReverse, setLoadingReverse] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [coverageData, setCoverageData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [fatCoverageError, setFatCoverageError] = useState(null);
  const [cablingProgress, setCablingProgress] = useState(0);
  const [isCablingRulesRunning, setIsCablingRulesRunning] = useState(false);
  const [serviceProviderPolygons, setServiceProviderPolygons] = useState(null);
  const [mapType, setMapType] = useState("roadmap");
  const [mapSettings, setMapSettings] = useState({
    // Map mode settings
    showTraffic: false,
    showTransit: false,
    showTerrain: false,
    hideMapLabels: false,
    // Satellite mode settings
    showLabels: true,
    showRoads: true,
    tilt45: false,
  });
  const abortControllerRef = useRef(null);

  // Handler for map settings changes
  const handleSettingsChange = useCallback((newSettings) => {
    setMapSettings(newSettings);
  }, []);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="map-wrapper" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#ff0000", textAlign: "center" }}>
          <h2>خطا: کلید API گوگل مپ تنظیم نشده است</h2>
          <p>لطفاً REACT_APP_GOOGLE_MAPS_API_KEY را در فایل .env تنظیم کنید</p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-wrapper">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} language="fa" region="IR">
        <MapContent
          lat={lat} setLat={setLat}
          lng={lng} setLng={setLng}
          address={address} setAddress={setAddress}
          reverseAddress={reverseAddress} setReverseAddress={setReverseAddress}
          addressOptions={addressOptions} setAddressOptions={setAddressOptions}
          loadingAddress={loadingAddress} setLoadingAddress={setLoadingAddress}
          loadingReverse={loadingReverse} setLoadingReverse={setLoadingReverse}
          error={error} setError={setError}
          selectedProvince={selectedProvince} setSelectedProvince={setSelectedProvince}
          selectedCity={selectedCity} setSelectedCity={setSelectedCity}
          coverageData={coverageData} setCoverageData={setCoverageData}
          isCalculating={isCalculating} setIsCalculating={setIsCalculating}
          fatCoverageError={fatCoverageError} setFatCoverageError={setFatCoverageError}
          serviceProviderPolygons={serviceProviderPolygons} setServiceProviderPolygons={setServiceProviderPolygons}
          cablingProgress={cablingProgress} setCablingProgress={setCablingProgress}
          isCablingRulesRunning={isCablingRulesRunning} setIsCablingRulesRunning={setIsCablingRulesRunning}
          abortControllerRef={abortControllerRef}
          mapType={mapType} setMapType={setMapType}
          mapSettings={mapSettings} onSettingsChange={handleSettingsChange}
        />
      </APIProvider>
    </div>
  );
}
